"use client";
import { cn, stringToRgbColor } from "@lib/utils";
import { Avatar, AvatarFallback } from "../primitives/avatar";
import { VolumeOff } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import CachedAvatarImage from "@/components/shared/CachedAvatarImage";
import UserAvatar from "@/components/shared/UserAvatar";

interface ChatAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  chatId: string;
  chatName: string;
  attachmentId?: string;
  secondMemberId?: string;
  isMuted?: boolean;
}

export default function ChatAvatar({
  chatId,
  attachmentId,
  chatName,
  secondMemberId,
  isMuted,
  className,
}: ChatAvatarProps) {
  const { t } = useTranslation();
  const avatarColor = useMemo(() => stringToRgbColor(chatId), [chatId]);

  // P2P chats: delegate to UserAvatar for live avatar resolution
  const isP2P = !!secondMemberId;

  return (
    <div className="relative shrink-0">
      {isP2P ? (
        <UserAvatar
          username={secondMemberId}
          fallbackAttachmentId={attachmentId}
          className={cn("size-10 text-sm", className)}
          aria-label={t("aria.chatAvatar")}
        />
      ) : (
        <Avatar
          aria-label={t("aria.chatAvatar")}
          style={{ backgroundColor: `rgba(${avatarColor}, 0.7)` }}
          className={cn("size-10 text-sm", className)}
        >
          <CachedAvatarImage attachmentId={attachmentId} />
          <AvatarFallback className="bg-transparent">
            {chatName.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      )}
      {isMuted && (
        <div className="absolute -right-1 -top-2 flex size-5 items-center justify-center rounded-full bg-card align-middle">
          <VolumeOff className="size-3 text-foreground" />
        </div>
      )}
    </div>
  );
}
